import type { KioskRenderDocument, KioskVisibilityRule, KioskWidgetPosition } from '@cluster/types';
import type { RendererIssue } from './errors';
import { FROZEN_RENDER_GEOMETRY as GEOMETRY } from './geometry';
import { parseKioskRenderDocument } from './parse';
import type { VisibilityCollection } from './parse';
import type { WidgetRenderContract } from './registry';
import { lookupWidgetRenderContract } from './registry';

/** Pixel rect in canvas space (origin top-left, CSS pixels — the frozen units). */
export interface ResolvedRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** Maps the frozen position shape to paint coordinates without altering numbers. */
export function toRect(position: KioskWidgetPosition): ResolvedRect {
  return { left: position.x, top: position.y, width: position.w, height: position.h };
}

/** A widget plus its deterministic paint slot. */
export interface StackedWidget {
  readonly widget: KioskRenderDocument['layout'][number];
  readonly rect: ResolvedRect;
  /** 0 = painted first (back); higher paints on top. */
  readonly stackIndex: number;
}

/**
 * Deterministic painting order: ascending `zIndex`, ties broken by source
 * order in the layout array (stable). Same input document ⇒ same output, in
 * any host runtime.
 */
export function resolveStackingOrder(document: KioskRenderDocument): StackedWidget[] {
  return document.layout
    .map((widget, sourceIndex) => ({ widget, sourceIndex }))
    .sort((a, b) => {
      const byZ = a.widget.zIndex - b.widget.zIndex;
      if (byZ !== 0) return byZ;
      return a.sourceIndex - b.sourceIndex;
    })
    .map((entry, stackIndex) => ({
      widget: entry.widget,
      rect: toRect(entry.widget.position),
      stackIndex,
    }));
}

/** The widget that paints on top of the canvas, or `null` for an empty layout. */
export function topmostWidget(document: KioskRenderDocument): StackedWidget | null {
  const order = resolveStackingOrder(document);
  return order.length > 0 ? order[order.length - 1] : null;
}

/**
 * Uniform scale factor that fits the 1920×1080 authoring canvas into a
 * device viewport while preserving aspect ratio. Geometry math only — this
 * module never reads config values, so money strings can never reach it.
 */
export function computeViewportScale(viewport: { width: number; height: number }): number {
  if (
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    throw new TypeError('Viewport dimensions must be positive finite numbers');
  }
  return Math.min(viewport.width / GEOMETRY.canvas.width, viewport.height / GEOMETRY.canvas.height);
}

/** Applies a viewport scale to a rect, rounding to whole pixels deterministically. */
export function scaleRect(rect: ResolvedRect, scale: number): ResolvedRect {
  return {
    left: Math.round(rect.left * scale),
    top: Math.round(rect.top * scale),
    width: Math.round(rect.width * scale),
    height: Math.round(rect.height * scale),
  };
}

/** `true` when the document is addressed to the kiosk channel (the frozen value). */
export function isKioskChannel(document: KioskRenderDocument): boolean {
  return document.visibility.channel === 'kiosk';
}

function contains(list: readonly string[], recordId: string): boolean {
  return list.includes(recordId);
}

/**
 * Effective visibility of one record against a frozen visibility rule:
 * `denyIds` always wins; an empty `allowIds` means "all otherwise-eligible
 * records" per the contract comment in `@cluster/types`.
 */
export function isRuleVisible(rule: KioskVisibilityRule, recordId: string): boolean {
  const id = recordId.trim();
  if (id.length === 0) return false;
  if (contains(rule.denyIds, id)) return false;
  if (rule.allowIds.length === 0) return true;
  return contains(rule.allowIds, id);
}

/** Convenience over the document's named collections. */
export function isRecordVisible(
  document: KioskRenderDocument,
  collection: VisibilityCollection,
  recordId: string,
): boolean {
  return isRuleVisible(document.visibility[collection], recordId);
}

/** A parsed widget bound to its registry contract, ready for host rendering. */
export interface PlacedWidget {
  readonly widget: KioskRenderDocument['layout'][number];
  readonly contract: WidgetRenderContract;
  readonly rect: ResolvedRect;
  readonly stackIndex: number;
}

export interface RenderPlan {
  readonly document: KioskRenderDocument;
  /** Paint order, back to front; every entry has a known registry contract. */
  readonly widgets: readonly PlacedWidget[];
}

export type RenderPlanOutcome = { ok: true; plan: RenderPlan } | { ok: false; issues: readonly RendererIssue[] };

/**
 * One-shot fail-closed pipeline for consumers (T05 kiosk, T06 preview):
 * parse the wire document, resolve stacking, and bind every widget to its
 * registry contract. Any structural issue — including an unknown widget
 * identifier — rejects the whole document instead of rendering partial,
 * broken actions.
 */
export function prepareRenderPlan(value: unknown): RenderPlanOutcome {
  const parsed = parseKioskRenderDocument(value);
  if (!parsed.ok) return { ok: false, issues: parsed.issues };

  const stacking = resolveStackingOrder(parsed.value);
  const missing: RendererIssue[] = [];
  const widgets: PlacedWidget[] = [];
  for (const stacked of stacking) {
    const contract = lookupWidgetRenderContract(stacked.widget.type);
    if (!contract) {
      // Defensive: parse already rejects unknown types. If this ever fires it
      // is registry/catalog drift and must fail closed, never skip.
      missing.push({
        path: `layout.${stacked.widget.widgetId}.type`,
        code: 'UNKNOWN_WIDGET_TYPE',
        message: `Widget type "${stacked.widget.type}" has no renderer contract registered`,
      });
      continue;
    }
    widgets.push({ widget: stacked.widget, contract, rect: stacked.rect, stackIndex: stacked.stackIndex });
  }
  if (missing.length > 0) return { ok: false, issues: missing };

  return { ok: true, plan: { document: parsed.value, widgets } };
}
